import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Heart, CreditCard, MapPin, Calendar, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import BurgerMenu from './BurgerMenu';
import SwipeableTourCard from './SwipeableTourCard';
import tourService from '../services/tourService';
import { useDistanceUnit } from '../contexts/DistanceUnitContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLocationCoords } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';

interface HomeProps {
  userName: string;
  credits: number;
}

interface NearbyTour {
  tourId: string;
  title: string;
  description?: string;
  streetName?: string;
  city?: string;
  country?: string;
  formattedAddress?: string;
  distanceFromUser?: number; // in meters
  distanceFromUserFormatted?: string; // formatted by API (we'll override this)
  estimatedDuration?: number;
  viewCount: number;
  saveCount?: number;
  createdAt: string;
  audioStatus: string;
}

const Home: React.FC<HomeProps> = ({ userName, credits }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatDistance } = useDistanceUnit();
  const { isDarkMode } = useTheme();
  const { latitude, longitude, error: locationError } = useLocationCoords();
  const hasInitiallyFetchedRef = useRef(false);
  const { user } = useAuth();

  // Check if user is logged in
  const isLoggedIn = !!user;

  // Animation state for press and hold
  const [pressedCard, setPressedCard] = useState<number | null>(null);

  // Handler for "Craft New Tour" - check if logged in first
  const handleCraftNewTour = () => {
    if (!isLoggedIn) {
      // Not logged in - redirect to login
      navigate('/');
      return;
    }
    
    if (credits <= 0) {
      // No credits - redirect to buy credits
      navigate('/buy-credits', {
        state: { 
          message: 'Your balance is insufficient, please\npurchase credits and try again.',
          returnTo: '/location' 
        }
      });
    } else {
      // Has credits - proceed to location selection
      navigate('/location');
    }
  };

  // Handler for Collection - redirect to login if not logged in
  const handleCollection = () => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    navigate('/past-tours');
  };

  // Handler for Buy Tours - redirect to login if not logged in
  const handleBuyTours = () => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    navigate('/buy-credits');
  };

  const mainCards = [
    {
      id: 'talk',
      title: 'Create A Tour',
      subtitle: "Tailored to your own\ninterests & locations",
      icon: <Sparkles size={24} />,
      onClick: handleCraftNewTour,
      color: '#6366F1',
      textColor: '#FFFFFF'
    },
    {
      id: 'new-chat',
      title: 'Collection',
      icon: <Heart size={24} />,
      onClick: handleCollection,
      color: '#10B981',
      textColor: '#FFFFFF'
    },
    {
      id: 'search',
      title: 'Buy Tours',
      icon: <CreditCard size={24} />,
      onClick: handleBuyTours,
      color: '#F59E0B',
      textColor: '#FFFFFF',
      isFullWidth: true
    }
  ];

  const [nearbyTours, setNearbyTours] = useState<NearbyTour[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [totalTours, setTotalTours] = useState<number>(0);
  const [totalToursUnfiltered, setTotalToursUnfiltered] = useState<number>(0); // For showing all tours count to guests
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [showingPopularTours, setShowingPopularTours] = useState<boolean>(false);
  const [loadingPopularTours, setLoadingPopularTours] = useState<boolean>(false);

  // Reset scroll position when component mounts
  useEffect(() => {
    // Ensure we start at the top of the page
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
  }, []);

  // Fetch initial tours when component mounts or when navigating back to this page
  useEffect(() => {
    // Don't fetch tours if location access is denied
    if (locationError) {
      setLoading(false);
      return;
    }
    
    // Reset and fetch tours whenever we land on this page
    hasInitiallyFetchedRef.current = true;
    fetchTours(0, true);
    
    // Cleanup: reset the ref when component unmounts
    return () => {
      hasInitiallyFetchedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, locationError]);

  // Refetch tours when location becomes available (for better distance sorting)
  useEffect(() => {
    // Only refetch if we already fetched once and now have location data
    if (hasInitiallyFetchedRef.current && latitude !== undefined && longitude !== undefined) {
      fetchTours(0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  const handleShare = async (tour: NearbyTour) => {
    const shareUrl = `${window.location.origin}/tour/${tour.tourId}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: tour.title,
          text: `Check out this tour: ${tour.title}`,
          url: shareUrl,
        });
      } catch (err) {
        // Ignore abort errors silently
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Tour link copied to clipboard!');
      } catch (err) {
        // Silently fail
      }
    }
  };

  const fetchTours = async (offset: number = 0, isInitial: boolean = false) => {
    try {
      if (isInitial) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const result = await tourService.listTours(5, offset, {
        latitude,
        longitude,
        sortByDistance: !!(latitude && longitude),
        audioCompleted: !isLoggedIn // Only show tours with completed audio for guest users
      });

      if (isInitial) {
        setNearbyTours(result.tours);
        
        // For guest users, also fetch total count without audio filter to show correct "View More" count
        if (!isLoggedIn) {
          const unfilteredResult = await tourService.listTours(1, 0, {
            latitude,
            longitude,
            sortByDistance: false
          });
          setTotalToursUnfiltered(unfilteredResult.total);
        }
      } else {
        setNearbyTours((prev) => [...prev, ...result.tours]);
      }

      setTotalTours(result.total);
      setHasMore(offset + result.tours.length < result.total);
    } catch (error) {
      // Silently handle error
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchPopularTours = async () => {
    try {
      setLoadingPopularTours(true);
      
      // Fetch tours sorted by save_count (most liked/saved)
      const result = await tourService.listTours(10, 0, {
        audioCompleted: true // Only show completed tours
      });

      // Sort by save count in descending order (most popular first)
      const sortedTours = result.tours.sort((a: NearbyTour, b: NearbyTour) => {
        return (b.saveCount || 0) - (a.saveCount || 0);
      });

      setNearbyTours(sortedTours);
      setTotalTours(result.total);
      setShowingPopularTours(true);
      setHasMore(false); // Don't allow infinite scroll for popular tours
    } catch (error) {
      console.error('Error fetching popular tours:', error);
    } finally {
      setLoadingPopularTours(false);
    }
  };

  const loadMoreTours = useCallback(() => {
    if (loadingMore || !hasMore) return;
    fetchTours(nearbyTours.length, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, hasMore, nearbyTours.length]);

  const handleScroll = useCallback(() => {
    // Disable infinite scroll for non-logged-in users
    if (!isLoggedIn) return;
    
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
    
    // Increased trigger distance for earlier loading
    if (scrollHeight - scrollTop <= clientHeight + 300 && !loadingMore && hasMore) {
      loadMoreTours();
    }
  }, [loadingMore, hasMore, loadMoreTours, isLoggedIn]);

  useEffect(() => {
    // Only add scroll listener for logged-in users
    if (!isLoggedIn) return;
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isLoggedIn]);

  return (
    <>
      <Helmet>
        <title>Miaitor | Start An Adventure</title>
        <meta name="title" content="Miaitor | Start An Adventure" />
        <meta name="description" content="Miaitor generates tailored tours based on your interests & location" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://miaitor.com/home" />
        <meta property="og:title" content="Miaitor | Start An Adventure" />
        <meta property="og:description" content="Miaitor generates tailored tours based on your interests & location" />
        <meta property="og:image" content="https://miaitor.com/SocialShare.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://miaitor.com/home" />
        <meta name="twitter:title" content="Miaitor | Start An Adventure" />
        <meta name="twitter:description" content="Miaitor generates tailored tours based on your interests & location" />
        <meta name="twitter:image" content="https://miaitor.com/SocialShare.png" />
        
        <link rel="canonical" href="https://miaitor.com/home" />
      </Helmet>
    
      <div className="app">
        <div className="header">
          {/* Left side: miiaitor Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img 
            src={isDarkMode ? '/logo-dark.svg' : '/logo-light.svg'}
            alt="miiaitor"
            style={{
              height: '18px',
              width: 'auto',
              flexShrink: 0
            }}
          />
        </div>

        <div className="header-title"></div> {/* Empty title to maintain spacing */}

        {/* Right side: Burger Menu */}
        <BurgerMenu userName={userName} credits={credits} showUserInfo={true} />
      </div>

      <div className="container">
        {/* Greeting Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ marginBottom: '40px' }}
        >
          <h1 style={{ 
            fontSize: '36px', 
            fontWeight: '700',
            marginBottom: '8px',
            color: 'var(--text-primary)'
          }}>
            {isLoggedIn ? `Hey ${userName}` : 'Hello there..'}
          </h1>
          <p style={{ 
            fontSize: '18px', 
            color: 'var(--text-secondary)',
            margin: 0
          }}>
            Start your next adventure
          </p>
        </motion.div>

        {/* Main Cards Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1.6fr 1fr',
          gridTemplateRows: '1fr 1fr',
          gap: '16px',
          marginBottom: '48px',
          height: '320px'
        }}>
          {/* Talk with Cooper - Large card on left */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0 }}
            style={{
              gridColumn: '1',
              gridRow: '1 / 3'
            }}
          >
            <button
              onClick={mainCards[0].onClick}
              onMouseDown={() => setPressedCard(0)}
              onMouseUp={() => setPressedCard(null)}
              onMouseLeave={() => setPressedCard(null)}
              onTouchStart={() => setPressedCard(0)}
              onTouchEnd={() => setPressedCard(null)}
              style={{
                width: '100%',
                height: '100%',
                background: mainCards[0].color,
                border: 'none',
                borderRadius: '24px',
                padding: '20px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
              className="home-card-hover"
            >
              {/* Pattern layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='5' cy='5' r='3'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                pointerEvents: 'none',
                zIndex: 0
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Solid background behind icon */}
                <motion.div 
                  style={{
                    position: 'absolute',
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    backgroundColor: mainCards[0].color,
                    zIndex: 0
                  }}
                  animate={pressedCard === 0 ? {
                    rotate: [0, -10, 10, -10, 10, -10, 10, 0],
                  } : {
                    rotate: 0,
                  }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeInOut",
                    repeat: pressedCard === 0 ? Infinity : 0
                  }}
                />
                
                <motion.div 
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: mainCards[0].textColor,
                    position: 'relative',
                    zIndex: 1
                  }}
                  animate={pressedCard === 0 ? {
                    rotate: [0, -10, 10, -10, 10, -10, 10, 0],
                  } : {
                    rotate: 0,
                  }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeInOut",
                    repeat: pressedCard === 0 ? Infinity : 0
                  }}
                >
                  {mainCards[0].icon}
                </motion.div>
              </div>
              
              <div style={{ textAlign: 'left', position: 'relative', zIndex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  fontFamily: 'Rubik',
                  color: mainCards[0].textColor,
                  margin: 0,
                  marginBottom: '4px'
                }}>
                  {mainCards[0].title}
                </h3>
                <p style={{
                  fontSize: '16px',
                  color: mainCards[0].textColor,
                  opacity: 0.7,
                  margin: 0,
                  whiteSpace: 'pre-line'
                }}>
                  {mainCards[0].subtitle}
                </p>
              </div>
            </button>
          </motion.div>

          {/* New chat - Top right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            style={{
              gridColumn: '2',
              gridRow: '1'
            }}
          >
            <button
              onClick={mainCards[1].onClick}
              onMouseDown={() => setPressedCard(1)}
              onMouseUp={() => setPressedCard(null)}
              onMouseLeave={() => setPressedCard(null)}
              onTouchStart={() => setPressedCard(1)}
              onTouchEnd={() => setPressedCard(null)}
              style={{
                width: '100%',
                height: '100%',
                background: mainCards[1].color,
                border: 'none',
                borderRadius: '24px',
                padding: '20px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
              className="home-card-hover"
            >
              {/* Pattern layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0z' fill='none'/%3E%3Cpath d='M0 38 L40 0' stroke='%23ffffff' stroke-width='0.5' opacity='0.06'/%3E%3Cpath d='M0 18 L40 -20M0 58 L40 20' stroke='%23ffffff' stroke-width='0.5' opacity='0.06'/%3E%3C/svg%3E")`,
                pointerEvents: 'none',
                zIndex: 0
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Solid background behind icon */}
                <motion.div 
                  style={{
                    position: 'absolute',
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    backgroundColor: mainCards[1].color,
                    zIndex: 0
                  }}
                  animate={pressedCard === 1 ? {
                    rotate: [0, -10, 10, -10, 10, -10, 10, 0],
                  } : {
                    rotate: 0,
                  }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeInOut",
                    repeat: pressedCard === 1 ? Infinity : 0
                  }}
                />
                
                <motion.div 
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: mainCards[1].textColor,
                    position: 'relative',
                    zIndex: 1
                  }}
                  animate={pressedCard === 1 ? {
                    rotate: [0, -10, 10, -10, 10, -10, 10, 0],
                  } : {
                    rotate: 0,
                  }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeInOut",
                    repeat: pressedCard === 1 ? Infinity : 0
                  }}
                >
                  {mainCards[1].icon}
                </motion.div>
              </div>
              
              <div style={{ textAlign: 'left', position: 'relative', zIndex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  fontFamily: 'Rubik',
                  color: mainCards[1].textColor,
                  margin: 0
                }}>
                  {mainCards[1].title}
                </h3>
              </div>
            </button>
          </motion.div>

          {/* Search by image - Bottom right */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            style={{
              gridColumn: '2',
              gridRow: '2'
            }}
          >
            <button
              onClick={mainCards[2].onClick}
              onMouseDown={() => setPressedCard(2)}
              onMouseUp={() => setPressedCard(null)}
              onMouseLeave={() => setPressedCard(null)}
              onTouchStart={() => setPressedCard(2)}
              onTouchEnd={() => setPressedCard(null)}
              style={{
                width: '100%',
                height: '100%',
                background: mainCards[2].color,
                border: 'none',
                borderRadius: '24px',
                padding: '20px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
              className="home-card-hover"
            >
              {/* Pattern layer */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23ffffff' stroke-width='1' opacity='0.06'%3E%3Cpath d='M0 40 Q20 35, 40 40 T80 40'/%3E%3Cpath d='M0 20 Q20 15, 40 20 T80 20'/%3E%3Cpath d='M0 60 Q20 55, 40 60 T80 60'/%3E%3C/g%3E%3C/svg%3E")`,
                pointerEvents: 'none',
                zIndex: 0
              }} />
              
              <div style={{ position: 'relative', zIndex: 1 }}>
                {/* Solid background behind icon */}
                <motion.div 
                  style={{
                    position: 'absolute',
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    backgroundColor: mainCards[2].color,
                    zIndex: 0
                  }}
                  animate={pressedCard === 2 ? {
                    rotate: [0, -10, 10, -10, 10, -10, 10, 0],
                  } : {
                    rotate: 0,
                  }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeInOut",
                    repeat: pressedCard === 2 ? Infinity : 0
                  }}
                />
                
                <motion.div 
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: mainCards[2].textColor,
                    position: 'relative',
                    zIndex: 1
                  }}
                  animate={pressedCard === 2 ? {
                    rotate: [0, -10, 10, -10, 10, -10, 10, 0],
                  } : {
                    rotate: 0,
                  }}
                  transition={{ 
                    duration: 0.6,
                    ease: "easeInOut",
                    repeat: pressedCard === 2 ? Infinity : 0
                  }}
                >
                  {mainCards[2].icon}
                </motion.div>
              </div>
              
              <div style={{ textAlign: 'left', position: 'relative', zIndex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  fontFamily: 'Rubik',
                  color: mainCards[2].textColor,
                  margin: 0
                }}>
                  {mainCards[2].title}
                </h3>
              </div>
            </button>
          </motion.div>
        </div>

        {/* Community Tours Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <h2 
              onClick={() => {
                if (isLoggedIn && !showingPopularTours) {
                  navigate('/community-tours-map');
                }
              }}
              style={{
                fontSize: '20px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                margin: 0,
                cursor: (isLoggedIn && !showingPopularTours) ? 'pointer' : 'default',
                transition: 'color 0.2s ease',
                userSelect: 'none'
              }}
              onMouseEnter={(e) => {
                if (isLoggedIn && !showingPopularTours) {
                  e.currentTarget.style.color = 'var(--primary-color)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-primary)';
              }}
            >
              {showingPopularTours ? 'Popular Tours' : 'Community Tours'}
            </h2>
            {!locationError && totalTours > 0 && isLoggedIn && !showingPopularTours && (
              <span 
                onClick={() => navigate('/community-tours-map')}
                style={{
                  fontSize: '14px',
                  color: 'var(--primary-color)',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                View Map
              </span>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            {locationError && !showingPopularTours ? (
              // Location permission denied/unavailable empty state
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center',
                color: 'var(--text-secondary)'
              }}>
                <div>
                  <MapPin size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <div style={{ fontSize: '15px', marginBottom: '8px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {locationError === 'location_denied' 
                      ? 'Location access required'
                      : 'Geolocation not supported'}
                  </div>
                  <div style={{ fontSize: '13px', marginBottom: '20px', opacity: 0.8 }}>
                    {locationError === 'location_denied' 
                      ? 'We need a location to display nearby tours'
                      : 'Use a modern browser to see nearby tours'}
                  </div>
                  <motion.button
                    onClick={fetchPopularTours}
                    disabled={loadingPopularTours}
                    whileTap={{ scale: 0.95 }}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: loadingPopularTours ? 'not-allowed' : 'pointer',
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      opacity: loadingPopularTours ? 0.6 : 1
                    }}
                  >
                    {loadingPopularTours ? 'Loading...' : 'Show Popular Tours'}
                  </motion.button>
                </div>
              </div>
            ) : loading ? (
              // Initial loading skeleton - these will morph into real cards
              <>
                {[1, 2, 3, 4].map((skeletonIndex) => (
                  <motion.div
                    key={`skeleton-${skeletonIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: skeletonIndex * 0.05 }}
                    style={{
                      padding: '16px',
                      backgroundColor: 'var(--card-background)',
                      borderRadius: '16px',
                      marginBottom: '16px',
                      boxShadow: 'var(--shadow-sm)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Skeleton title */}
                    <div style={{
                      height: '20px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      width: '70%',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }} />
                    
                    {/* Skeleton description */}
                    <div style={{
                      height: '14px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      width: '90%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.2s'
                    }} />
                    <div style={{
                      height: '14px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      width: '75%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.4s'
                    }} />
                    
                    {/* Skeleton metadata */}
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{
                        height: '14px',
                        backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                        borderRadius: '4px',
                        width: '60px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '0.6s'
                      }} />
                      <div style={{
                        height: '14px',
                        backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                        borderRadius: '4px',
                        width: '50px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '0.8s'
                      }} />
                      <div style={{
                        height: '14px',
                        backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                        borderRadius: '4px',
                        width: '45px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '1s'
                      }} />
                    </div>
                  </motion.div>
                ))}
              </>
            ) : nearbyTours.length === 0 ? (
              // No tours found
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
                color: 'var(--text-secondary)'
              }}>
                <p style={{ fontSize: '16px', marginBottom: '8px' }}>No tours nearby</p>
                <p style={{ fontSize: '14px', marginBottom: '24px' }}>Be the first to craft one!</p>
                <motion.button
                  onClick={() => navigate('/location')}
                  whileTap={{ 
                    backgroundColor: 'rgba(67, 56, 202, 1)',
                    transition: { duration: 0 }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    backgroundColor: '#4F46E5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                >
                  <Sparkles size={16} />
                  Create A Tour
                </motion.button>
              </div>
            ) : (
              // Display tours
              <>
                <AnimatePresence mode="popLayout">
                  {/* Show first 3 cards normally */}
                  {nearbyTours.slice(0, 3).map((tour: NearbyTour, index: number) => {
                    const tourDate = new Date(tour.createdAt);
                    const address = tour.formattedAddress || 
                                   `${tour.streetName || ''}${tour.city ? ', ' + tour.city : ''}${tour.country ? ', ' + tour.country : ''}` ||
                                   'Address not available';
                    
                    return (
                      <motion.div
                        key={tour.tourId}
                        initial={{ opacity: 0 }}
                        animate={{ 
                          opacity: 1,
                          transition: { 
                            opacity: { duration: 0.4, delay: index * 0.1 }
                          }
                        }}
                        exit={{ opacity: 0, height: 0, marginBottom: 0, transition: { duration: 0.2 } }}
                        style={{
                          marginBottom: (index === 2 && nearbyTours.length === 3 && !loadingMore) ? '0' : '16px',
                        }}
                      >
                        <SwipeableTourCard
                          onShare={() => handleShare(tour)}
                          onClick={() => {
                            navigate(`/tour/${tour.tourId}`, {
                              state: { fromHome: true, isNearbyTour: true }
                            });
                          }}
                        >
                          <div style={{ padding: '16px', position: 'relative' }}>
                            <div style={{ marginBottom: '8px', pointerEvents: 'none' }}>
                              <h3 style={{ fontSize: '18px', marginBottom: '4px', color: 'var(--text-primary)' }}>
                                {tour.title}
                              </h3>
                            </div>
                            
                            <p style={{ 
                              fontSize: '14px', 
                              color: 'var(--text-secondary)',
                              marginBottom: '12px',
                              lineHeight: '1.4',
                              pointerEvents: 'none'
                            }}>
                              {address}
                            </p>
                            
                            <div style={{ display: 'flex', gap: '16px', color: 'var(--text-secondary)', flexWrap: 'wrap', pointerEvents: 'none' }}>
                              {tour.distanceFromUser !== undefined && (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <MapPin size={16} />
                                  <span style={{ fontSize: '14px' }}>{formatDistance(tour.distanceFromUser)}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <Calendar size={16} />
                                <span style={{ fontSize: '14px' }}>
                                  {tourDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '/')}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <Heart size={16} />
                                <span style={{ fontSize: '14px' }}>{(tour.saveCount || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </SwipeableTourCard>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
                
                <div style={{ position: 'relative' }}>
                  
                  {/* For non-logged-in users: show blurred remaining cards with signup overlay */}
                  {!isLoggedIn && nearbyTours.length > 3 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.8 }}
                      style={{ position: 'relative' }}
                    >
                      {/* Blurred preview cards */}
                      <div style={{
                        filter: 'blur(4px)',
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        maxHeight: '140px',
                        maskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black 0%, transparent 100%)'
                      }}>
                        {nearbyTours.slice(3, 6).map((tour: NearbyTour) => {
                          const tourDate = new Date(tour.createdAt);
                          const address = tour.formattedAddress || 
                                         `${tour.streetName || ''}${tour.city ? ', ' + tour.city : ''}${tour.country ? ', ' + tour.country : ''}` ||
                                         'Address not available';
                          
                          return (
                            <div
                              key={tour.tourId}
                              style={{
                                padding: '16px',
                                borderRadius: '16px',
                                backgroundColor: 'var(--card-background)',
                                boxShadow: 'var(--shadow-sm)',
                                marginBottom: '16px'
                              }}
                            >
                              <div style={{ marginBottom: '8px' }}>
                                <h3 style={{ fontSize: '18px', marginBottom: 0, color: 'var(--text-primary)' }}>{tour.title}</h3>
                              </div>
                              
                              <p style={{ 
                                fontSize: '14px', 
                                color: 'var(--text-secondary)',
                                marginBottom: '12px',
                                lineHeight: '1.4'
                              }}>
                                {address}
                              </p>
                              
                              <div style={{ display: 'flex', gap: '16px', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                                {tour.distanceFromUser !== undefined && (
                                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                    <MapPin size={16} />
                                    <span style={{ fontSize: '14px' }}>{formatDistance(tour.distanceFromUser)}</span>
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <Calendar size={16} />
                                  <span style={{ fontSize: '14px' }}>
                                    {tourDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '/')}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <Heart size={16} />
                                  <span style={{ fontSize: '14px' }}>{(tour.saveCount || 0).toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Join For More button overlay */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.8 }}
                        style={{
                          position: 'absolute',
                          bottom: '20px',
                          left: '0',
                          right: '0',
                          zIndex: 10,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}
                      >
                        <button
                          onClick={() => navigate('/')}
                          style={{
                            padding: '16px 32px',
                            backgroundColor: 'var(--primary-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
                            transition: 'all 0.3s ease'
                          }}
                          className="login-button-hover"
                        >
                          View +{(totalToursUnfiltered || totalTours) - 3} More Tours
                        </button>
                      </motion.div>
                    </motion.div>
                  )}
                  
                  {/* For logged-in users: show all remaining tours */}
                  {isLoggedIn && nearbyTours.slice(3).map((tour: NearbyTour, index: number) => {
                    const actualIndex = index + 3;
                    const isInitialCard = actualIndex < 5; // First 5 cards get cascading animation
                    const animationDelay = isInitialCard ? actualIndex * 0.1 : 0;
                    const tourDate = new Date(tour.createdAt);
                    const address = tour.formattedAddress || 
                                   `${tour.streetName || ''}${tour.city ? ', ' + tour.city : ''}${tour.country ? ', ' + tour.country : ''}` ||
                                   'Address not available';
                    
                    return (
                      <motion.div
                        key={tour.tourId}
                        initial={{ opacity: 0 }}
                        animate={{ 
                          opacity: 1,
                          transition: { 
                            opacity: { duration: 0.4, delay: animationDelay }
                          }
                        }}
                        style={{
                          marginBottom: actualIndex === nearbyTours.length - 1 && !loadingMore ? '0' : '16px',
                        }}
                      >
                        <SwipeableTourCard
                          onShare={() => handleShare(tour)}
                          onClick={() => {
                            navigate(`/tour/${tour.tourId}`, {
                              state: { fromHome: true, isNearbyTour: true }
                            });
                          }}
                        >
                          <div style={{ padding: '16px', position: 'relative' }}>
                            <div style={{ marginBottom: '8px', pointerEvents: 'none' }}>
                              <h3 style={{ fontSize: '18px', marginBottom: '4px', color: 'var(--text-primary)' }}>
                                {tour.title}
                              </h3>
                            </div>
                            
                            <p style={{ 
                              fontSize: '14px', 
                              color: 'var(--text-secondary)',
                              marginBottom: '12px',
                              lineHeight: '1.4',
                              pointerEvents: 'none'
                            }}>
                              {address}
                            </p>
                            
                            <div style={{ display: 'flex', gap: '16px', color: 'var(--text-secondary)', flexWrap: 'wrap', pointerEvents: 'none' }}>
                              {tour.distanceFromUser !== undefined && (
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                  <MapPin size={16} />
                                  <span style={{ fontSize: '14px' }}>{formatDistance(tour.distanceFromUser)}</span>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <Calendar size={16} />
                                <span style={{ fontSize: '14px' }}>
                                  {tourDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' }).replace(/\//g, '/')}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <Heart size={16} />
                                <span style={{ fontSize: '14px' }}>{(tour.saveCount || 0).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        </SwipeableTourCard>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}
            
            {/* Loading more skeleton - only for logged in users */}
            {loadingMore && !locationError && isLoggedIn && (
              <>
                {[1, 2, 3, 4].map((skeletonIndex) => (
                  <motion.div
                    key={`skeleton-more-${skeletonIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.2, delay: skeletonIndex * 0.05 }}
                    style={{
                      padding: '16px',
                      backgroundColor: 'var(--card-background)',
                      borderRadius: '16px',
                      marginBottom: '16px',
                      border: '1px solid var(--border-color)',
                      boxShadow: 'var(--shadow-sm)'
                    }}
                  >
                    {/* Skeleton title */}
                    <div style={{
                      height: '20px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      width: '70%',
                      animation: 'pulse 1.5s ease-in-out infinite'
                    }} />
                    
                    {/* Skeleton description */}
                    <div style={{
                      height: '14px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      width: '90%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.2s'
                    }} />
                    <div style={{
                      height: '14px',
                      backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      width: '75%',
                      animation: 'pulse 1.5s ease-in-out infinite',
                      animationDelay: '0.4s'
                    }} />
                    
                    {/* Skeleton metadata */}
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{
                        height: '14px',
                        backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                        borderRadius: '4px',
                        width: '60px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '0.6s'
                      }} />
                      <div style={{
                        height: '14px',
                        backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                        borderRadius: '4px',
                        width: '50px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '0.8s'
                      }} />
                      <div style={{
                        height: '14px',
                        backgroundColor: isDarkMode ? '#3A3A3A' : '#E5E7EB',
                        borderRadius: '4px',
                        width: '45px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: '1s'
                      }} />
                    </div>
                  </motion.div>
                ))}
              </>
            )}
            
            {loadingMore && !locationError && isLoggedIn && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: 'var(--text-secondary)'
              }}>
                <div className="spinner" style={{ margin: '0 auto' }}></div>
                <p style={{ marginTop: '12px', fontSize: '14px' }}>Loading more tours...</p>
              </div>
            )}

            {!loading && !loadingMore && !hasMore && nearbyTours.length > 0 && !locationError && isLoggedIn && (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: 'var(--text-secondary)',
                fontSize: '14px'
              }}>
                You've reached the end of the list
              </div>
            )}
          </div>
        </motion.div>


      </div>
    </div>
    </>
  );
};

export default Home; 